import * as React from 'react'
import AgreementTermsAndConditions from 'components/AgreementRequirement/agreements/AgreementTermsAndConditions/AgreementTermsAndConditions'
import classNames from 'classnames'
import get from 'lodash.get'
import Iframe from 'components/Iframe/Iframe'
import isEmpty from 'lodash/isEmpty'
import LoaderImage from 'components/loaders/LoaderImage/LoaderImage'
import TitleAndDescription from 'components/TitleAndDescription/TitleAndDescription'
import TraditionalContract from './ContractTraditional'
import { AppContext } from 'context/appContext'
import { Button } from '@conekta/frontino-ui/v2'
import { CONTACT_CONEKTA, EDOCSIGNPROVIDER_URL, WIZARD_STAGES_ID } from 'utils/constants'
import {
  CONTRACT_FILE_CLASSIFICATION_ID,
  IMG_CHECK_ICON,
  IMG_IFRAME_SHIELD,
  ONBOARDING_BACKEND_GENERAL_FILE_CLASSIFICATION_ID,
  ONBOARDING_BACKEND_PDF_FILE_TYPE_ID,
  SUCCESS_MESSAGE
} from './constants'
import { createIframeUrl, eDocSignProviderAPIData } from './helpers'
import { entitiesUploadFilePost } from 'lib/entities'
import { getToken, getUserSessionDataFromLocalStorage } from 'utils/auth'
import { IContract } from './interface'
import { IFormSubmitErrors, IMimeTypes } from '../../../Documents/interface'
import { IFTTT } from 'utils/misc'
import { UIContext } from 'shared/context/UIContext'
import {
  useContractSendUrlPost,
  useEDocSignProviderEContractCreatePost,
  useEDocSignProviderEContractGet
} from 'state/contract'
import { useFileTypesGet } from 'state/files'
import { useTranslation } from 'react-i18next'
import './Contract.scss'

// Contract
// The merchant can choose between Traditional contract or e-contract.
//
// The traditional contract option should:
// 1.- Mount a modal with a form with instructions on traditional sign
// 2.- Send the traditional signed contract of the form to the backend
// 3.- Set the state of the component to 'signed'
//
// The e-contract option should:
// 1.- Mount a modal with waiting instructions while the petition to the
// provider is being sent.
// 2.- Mount an iframe with the url from the response of the provider
// 3.- Set the state of the component to 'signed'
// 4.- GET the e-contract from provider to obtain the address
// from the signed contract
// 5.- POST the signed contract address to the backend in order to be saved

const { useContext, useEffect, useState } = React
const T_ROOT = 'contract'

export const Contract = ({ onAccept, status }: IContract): JSX.Element => {
  const { fileTypesGet } = useFileTypesGet()
  const [eContractGenerated, setEContractGenerated] = useState<boolean>(false)
  const [esign, setEsign] = useState<boolean>(false)
  const [signed, setSigned] = useState<boolean>(false)
  const { appStore, wizardData, wizardSavedData } = useContext(AppContext)
  const { createModal, removeModal } = useContext(UIContext)
  const { support_email } = CONTACT_CONEKTA
  const { t } = useTranslation()
  const { contractSendUrlPost, contractSendUrlPostReset } = useContractSendUrlPost()
  const { eDocSignProviderEContractCreatePost, eDocSignProviderEContractCreatePostReset } =
    useEDocSignProviderEContractCreatePost()
  const { eDocSignProviderEContractGet, eDocSignProviderEContractGetReset } = useEDocSignProviderEContractGet()
  const E_CONTRACT_ID = get(appStore, 'eDocSingProviderEContractCreatePost.data[0].id', '')
  const MEXICAN_TAXPAYER_ID = get(wizardSavedData, `${WIZARD_STAGES_ID.information}.values.taxPayerRegister`, '')
  const E_CONTRACT_IFRAME_URL = createIframeUrl(E_CONTRACT_ID, MEXICAN_TAXPAYER_ID)
  const ENTITY_ID = wizardData.entityId
  const SIGNATURE_TYPE = wizardData.signatureType
  const SIGNED_E_CONTRACT = get(appStore, 'eDocSingProviderEContractGet.data[0].signaturesCopyUrl', '')
  const SIGNED_E_CONTRACT_POST_DATA = {
    fileClassificationId: ONBOARDING_BACKEND_GENERAL_FILE_CLASSIFICATION_ID,
    fileTypeId: ONBOARDING_BACKEND_PDF_FILE_TYPE_ID,
    url: SIGNED_E_CONTRACT
  }
  const USER_SESSION_DATA = getUserSessionDataFromLocalStorage()
  useEffect(() => {
    fileTypesGet()
    return () => {
      contractSendUrlPostReset()
      eDocSignProviderEContractCreatePostReset()
      eDocSignProviderEContractGetReset()
    }
  }, [])

  useEffect(() => {
    setEsign(get(wizardSavedData, `${WIZARD_STAGES_ID.documents}.esign`, false))
  }, [])

  useEffect(() => {
    window.addEventListener
      ? window.addEventListener('message', handleEventMessageFromIframe)
      : console.error('Event listener not supported')
    return () => {
      window.removeEventListener('message', handleEventMessageFromIframe)
    }
  }, [signed])

  useEffect(() => {
    IFTTT(get(appStore, 'eDocSingProviderEContractCreatePost.data', '').length, () => {
      setEContractGenerated(true)
    })
  }, [appStore.eDocSingProviderEContractCreatePost.data])

  useEffect(() => {
    if (eContractGenerated) {
      handleEdocSignProviderEContractModal()
    }
  }, [eContractGenerated])

  useEffect(() => {
    if (E_CONTRACT_ID && signed) {
      setTimeout(() => eDocSignProviderEContractGet(E_CONTRACT_ID), 3000)
    }
  }, [signed])

  useEffect(() => {
    if (SIGNED_E_CONTRACT) {
      const SELECTED_COMPANY_ID = wizardData.entityId
      contractSendUrlPost(SIGNED_E_CONTRACT_POST_DATA, SELECTED_COMPANY_ID)
    }
  }, [SIGNED_E_CONTRACT])

  const handleEventMessageFromIframe = (event: any) => {
    let message
    if (event.origin === EDOCSIGNPROVIDER_URL) {
      message = event.data
      message === SUCCESS_MESSAGE ? handleEcontractAfterSign() : console.error(message)
    }
  }

  const handleEcontractAfterSign = () => {
    setSigned(true)
  }

  // E-Sign contract methods
  const handleEdocSignProviderEContractCreation = () => {
    if (eContractGenerated) {
      handleEdocSignProviderEContractModal()
    } else {
      eDocSignProviderEContractCreatePost(eDocSignProviderAPIData)
      createSafeSignModal()
    }
  }

  const handleEdocSignProviderEContractModal = () => {
    removeModal()
    createModal(<Iframe url={E_CONTRACT_IFRAME_URL} />, {
      className: 'Contract__modal'
    })
  }

  // Traditional contract methods
  const handleTraditionalContractCreation = () => {
    createModal(
      <TraditionalContract
        handleCancel={() => removeModal()}
        handleSubmit={handleTraditionalContractSubmit}
        fileTypes={appStore.fileTypesGet.data}
      />,
      {
        className: 'ContractTraditional__Modal',
        title: t(`${T_ROOT}.traditionalContract.title`)
      }
    )
  }

  const handleTraditionalContractSubmit = async (value: any) => {
    let errors: IFormSubmitErrors = {}
    const TRADITIONAL_CONTRACT_SIGNED = value.contract
    const FILE_TYPE = appStore.fileTypesGet.data.find(
      ({ mimeType }: IMimeTypes) => mimeType === TRADITIONAL_CONTRACT_SIGNED.type
    )
    const VALUES = {
      data: TRADITIONAL_CONTRACT_SIGNED,
      fileClassification: CONTRACT_FILE_CLASSIFICATION_ID,
      fileTypeId: FILE_TYPE.id,
      id: ENTITY_ID,
      token: getToken()
    }

    await entitiesUploadFilePost({ ...VALUES }).catch((error: any) => {
      const ERRORS = get(error.response, 'data.errors', [])
      errors = get(ERRORS[0], 'message', 'Error')
    })

    if (isEmpty(errors)) {
      setSigned(true)
      onAccept(true)
      removeModal()
    } else {
      return errors
    }
  }

  const createSafeSignModal = () => {
    createModal(
      <>
        <LoaderImage className='Contract__LoaderImage' src={IMG_IFRAME_SHIELD} />

        <TitleAndDescription
          className='SafeSignText__modal'
          title={t(`${T_ROOT}.safeSignModal.title`)}
          description={t(`${T_ROOT}.safeSignModal.description`)}
        />
      </>,
      {
        className: 'LoaderImage__modal'
      }
    )
  }

  const Signed = () => {
    removeModal()
    return (
      <>
        <div className='row Contract__successful-sign-text'>
          <img src={IMG_CHECK_ICON} alt={''} />
          <p>{t(`${T_ROOT}.signConfirmation`)}</p>
        </div>
      </>
    )
  }

  const TermsAndConditions = () => (
    <div className='ContractTermsAndConditions'>
      <AgreementTermsAndConditions checked={status} className='ContractTC' linkColor='indigo' onChange={onAccept} />
    </div>
  )

  const SignButton = ({ onClick }: { onClick: () => void }): React.ReactElement => (
    <Button onClick={onClick} margin='10px 0' variant='primaryBlue'>
      {t(`${T_ROOT}.signButton`)}
    </Button>
  )

  const EContractAndContract = () => {
    const ERROR_MESSAGE = get(appStore, 'eDocSingProviderEContractCreatePost.error.message')
    return (
      <div className={classNames('ContractES')}>
        <p className='Contract__greeting'>{get(USER_SESSION_DATA, 'profile.given_name', false)},</p>

        <div className='Contract__text'>
          <p>
            {t(`${T_ROOT}.content`)}
            <span>{support_email}</span>
          </p>
          <p>{t(`${T_ROOT}.agreement`)}</p>
        </div>

        <div className='row Contract__sign'>
          {signed ? (
            <Signed />
          ) : (
            <div className='Contract__not-signed-sign'>
              {esign ? (
                <SignButton onClick={handleEdocSignProviderEContractCreation} />
              ) : (
                <SignButton onClick={handleTraditionalContractCreation} />
              )}
              {ERROR_MESSAGE ? <p>{ERROR_MESSAGE}</p> : null}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={classNames('Contract')}>
      {SIGNATURE_TYPE === 'Contract' ? <EContractAndContract /> : <TermsAndConditions />}
    </div>
  )
}
